import { useState } from 'react';
import {
  ArrowLeftOutlined, CheckCircleOutlined, DiffOutlined, FileProtectOutlined, FileWordOutlined,
  MessageOutlined, ReloadOutlined, RobotOutlined, RollbackOutlined, ToolOutlined, UploadOutlined,
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';

import { PageHeader, Tile, TileBody, TileHeader } from '../../../components/shared';
import { Badge, Button, Empty, Skeleton } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useLoi } from '../../../hooks/useLoi';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { KHONG_CO, dungLuongVN, ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { BanDanhGiaAi } from '../components/BanDanhGiaAi';
import { HopApSua } from '../components/HopApSua';
import { HopTroLy } from '../components/HopTroLy';
import { DongKetQua } from '../components/DongKetQua';
import { HopNopLai } from '../components/HopNopLai';
import { HopSoSanh } from '../components/HopSoSanh';
import { HopYKien } from '../components/HopYKien';
import { VungDuLieu } from '../components/VungDuLieu';
import { KET_LUAN, QUYEN, TRANG_THAI_PHIEN } from '../constants';
import {
  useApDungSua, useBienBanPhien, useChamLaiPhien, useDanhGiaPhien, useHoanTatPhien, useKetQuaPhien,
  useLuotApSua, usePheDuyetPhien, usePhienChiTiet, useQuyetGoiYSua, useSuaKetQua, useVietLaiDanhGia,
} from '../hooks/useThamDinh';
import type { PhienChiTiet, TrangThaiPhien } from '../types';

/**
 * Chi tiết một hồ sơ (`/client/tham-dinh/ho-so/:id`): rà soát từng tiêu chí, hoàn tất, phê duyệt.
 *
 * Nút Hoàn tất **chặn khi còn dòng cần xem** (đặc tả mục 10.1) — nhưng chặn ở đây là ẩn nút kèm lời giải
 * thích, không phải để người dùng bấm rồi nhận 409. Backend vẫn kiểm lại: giao diện là tiện ích, không phải
 * chốt chặn.
 */
export function ChiTietHoSoView() {
  const { t, locale } = useT();
  const { id } = useParams<{ id: string }>();
  const dieuHuong = useDieuHuong();
  const dichLoi = useLoi();
  const q = useModulePermissions(QUYEN);

  const phien = usePhienChiTiet(id);
  const ketQua = useKetQuaPhien(id);
  const sua = useSuaKetQua();
  const hoanTat = useHoanTatPhien();
  const chamLai = useChamLaiPhien();
  const bienBan = useBienBanPhien();
  const danhGia = useDanhGiaPhien(id);
  const vietLai = useVietLaiDanhGia();
  const quyetSua = useQuyetGoiYSua();
  const apSua = useLuotApSua(id);
  const apDung = useApDungSua();
  const duyet = usePheDuyetPhien();
  const [hopYKien, setHopYKien] = useState<'duyet' | 'tra-lai' | undefined>();
  const [moNopLai, setMoNopLai] = useState(false);
  const [moSoSanh, setMoSoSanh] = useState(false);

  const ds = ketQua.data ?? [];
  /** Tên tiêu chí theo mã, để danh sách bỏ sót của bản đánh giá đọc được thay vì chỉ hiện mã. */
  const tenTheoMa = Object.fromEntries(ds.map((k) => [k.ma, k.ten]));
  const coGoiYSua = ds.some((k) => k.goi_y_sua);
  const soDongYSua = ds.filter((k) => k.sua_trang_thai === 'dong_y').length;
  const canXem = ds.filter((k) => k.can_xem);
  const conPhaiXem = canXem.filter((k) => !k.da_xem);
  const p = phien.data;

  /** Thanh tiến trình khi AI đang chấm; `buoc` nói đang làm gì (đặc tả mục 7.2). */
  function TienTrinh({ du }: { du: PhienChiTiet }) {
    if (!['cho_xu_ly', 'dang_doc', 'dang_cham'].includes(du.trang_thai)) return null;
    return (
      <div className="rounded-lg border border-primary-200 bg-primary-50 p-4">
        <div className="flex items-center justify-between gap-3">
          <p className="min-w-0 flex-1 truncate text-sm font-medium text-primary-800">{du.buoc}</p>
          <span className="shrink-0 text-sm text-primary-700">{soVN(du.tien_do, locale)}%</span>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-primary-100">
          <div className="h-full bg-primary-500 transition-all" style={{ width: `${du.tien_do}%` }} />
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="mt-2"
          icon={<ReloadOutlined />}
          onClick={() => { void phien.refetch(); void ketQua.refetch(); }}
        >
          {t('chung.lamMoi')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[
            { label: t('nav.nhom.thamDinhAi') },
            { label: t('nav.muc.hoSoThamDinh'), onClick: () => dieuHuong('/tham-dinh/ho-so') },
            { label: p?.tieu_de ?? '…' },
          ]}
          titleIcon={<FileProtectOutlined className="text-primary-600" />}
          title={p?.tieu_de ?? t('thamDinh.raSoat.dangTai')}
          subtitle={p ? `${p.bo_ten}${p.so_phien_ban !== null ? ` · v${soVN(p.so_phien_ban, locale)}` : ''}` : undefined}
          actions={
            <>
              <Button
                variant="ghost"
                size="md"
                icon={<ArrowLeftOutlined />}
                onClick={() => dieuHuong('/tham-dinh/ho-so')}
              >
                {t('thamDinh.raSoat.veDanhSach')}
              </Button>
              {/*
                * Tải biên bản: hiện cả khi hồ sơ chưa hoàn tất — biên bản khi đó mang nhãn BẢN NHÁP, và
                * chặn ở đây là buộc người thẩm định hoàn tất chỉ để đọc được bản in thử.
                */}
              {/*
                * So sánh: ẩn khi hồ sơ là lần nộp đầu — backend trả 409 cho trường hợp đó, và một nút bấm
                * xong để nhận lỗi là một nút không nên hiện (ADR-UI-0005).
                */}
              {p?.phien_truoc_id && (
                <Button
                  variant="ghost"
                  size="md"
                  icon={<DiffOutlined />}
                  onClick={() => setMoSoSanh(true)}
                >
                  {t('thamDinh.raSoat.soSanh')}
                </Button>
              )}
              {/*
                * Nộp lại: chỉ khi hồ sơ đã qua bước chấm. Đang chấm thì backend chặn, và nộp lại một hồ sơ
                * chưa biết kết quả là chưa có gì để sửa.
                */}
              {q.phienTao && p && !p.thu_nghiem
                && ['cho_duyet', 'cho_phe_duyet', 'hoan_tat', 'loi'].includes(p.trang_thai) && (
                <Button
                  variant="ghost"
                  size="md"
                  icon={<UploadOutlined />}
                  onClick={() => setMoNopLai(true)}
                >
                  {t('thamDinh.raSoat.nopLai')}
                </Button>
              )}
              {q.phienXuat && p && (
                <Button
                  variant="ghost"
                  size="md"
                  icon={<FileWordOutlined />}
                  loading={bienBan.isPending}
                  onClick={() => bienBan.mutate({ id: p.id, tieuDe: p.tieu_de })}
                >
                  {t('thamDinh.raSoat.taiBienBan')}
                </Button>
              )}
              <Button
                variant="ghost"
                size="md"
                title={t('chung.lamMoi')}
                aria-label={t('chung.lamMoi')}
                onClick={() => { void phien.refetch(); void ketQua.refetch(); }}
              >
                <ReloadOutlined className={phien.isFetching ? 'animate-spin' : ''} />
              </Button>
            </>
          }
        />

        <VungDuLieu
          dangTai={phien.isPending}
          loi={phien.error}
          dulieu={p}
          rong={false}
          onThuLai={() => void phien.refetch()}
          rongTieuDe={t('thamDinh.phien.rong')}
        >
          {(du) => (
            <div className="space-y-4">
              <TienTrinh du={du} />

              {/*
                * Lượt chấm lỗi: câu lỗi của backend đã nói cách xử lý, nên chỗ này chỉ thêm đường ra. Nút
                * ẩn khi không có quyền sửa (ADR-UI-0005) — chấm lại tiêu một lượt gọi mô hình.
                */}
              {du.trang_thai === 'loi' && du.loi && (
                <div className="rounded-lg border border-danger-200 bg-danger-50 p-4">
                  <p className="font-medium text-danger-800">{t('thamDinh.raSoat.chamLoi')}</p>
                  <p className="mt-1 text-sm text-danger-700">{du.loi}</p>
                  {q.phienSua && (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="mt-3"
                      icon={<ReloadOutlined />}
                      loading={chamLai.isPending}
                      onClick={() => chamLai.mutate(du.id)}
                    >
                      {t('thamDinh.raSoat.chamLai')}
                    </Button>
                  )}
                </div>
              )}

              {/* Cảnh báo mức hồ sơ: hồ sơ bị cắt, tiêu chí AI chưa chấm được. */}
              {du.canh_bao.length > 0 && (
                <ul className="space-y-1 rounded-lg border border-warning-200 bg-warning-50 p-3">
                  {du.canh_bao.map((c) => (
                    <li key={c} className="text-sm text-warning-800">{c}</li>
                  ))}
                </ul>
              )}

              <div className="grid grid-cols-12 gap-4">
                <Tile span="lg:col-span-8">
                  <TileHeader
                    icon={<FileProtectOutlined />}
                    tone="primary"
                    title={t('thamDinh.raSoat.ketQua')}
                    mo={canXem.length > 0
                      ? t('thamDinh.raSoat.conPhaiXem', {
                        con: soVN(conPhaiXem.length, locale), tong: soVN(canXem.length, locale),
                      })
                      : undefined}
                  />
                  <TileBody className="p-0">
                    {ketQua.isPending ? (
                      <div className="space-y-2 p-4" aria-busy="true">
                        <Skeleton className="h-9 w-full" />
                        <Skeleton className="h-9 w-full" />
                      </div>
                    ) : ds.length === 0 ? (
                      <Empty
                        title={t('thamDinh.raSoat.chuaCham')}
                        description={t('thamDinh.raSoat.chuaChamMo')}
                      />
                    ) : (
                      <ul>
                        {ds.map((k) => (
                          <DongKetQua
                            key={k.ma}
                            kq={k}
                            duocSua={q.phienSua && du.trang_thai === 'cho_duyet'}
                            dangLuu={sua.isPending}
                            onSua={(d) => sua.mutate({ phienId: du.id, ma: k.ma, du: d })}
                            duocQuyetSua={q.phienSua}
                            onQuyetSua={(dongY) =>
                              quyetSua.mutate({ phienId: du.id, ma: k.ma, dongY })}
                          />
                        ))}
                      </ul>
                    )}
                  </TileBody>
                </Tile>

                {/*
                  * Bản đánh giá nằm ở cột chính, DƯỚI bảng kết quả: nó tổng hợp bảng ấy, nên đọc bảng trước
                  * rồi đọc tổng hợp. Chỉ hiện khi hồ sơ đã chấm — trước đó không có gì để tổng hợp.
                  */}
                {/*
                  * Áp gợi ý sửa: chỉ hiện khi hồ sơ có gợi ý nào. Không có gợi ý thì cả khối vô nghĩa, và
                  * một nút bấm xong để nhận 409 là một nút không nên hiện (ADR-UI-0005).
                  */}
                {coGoiYSua && q.troLyVietLai && (
                  <Tile span="lg:col-span-8">
                    <TileHeader icon={<ToolOutlined />} tone="orange" title={t('thamDinh.raSoat.apSua')} />
                    <TileBody>
                      <HopApSua
                        luot={apSua.data}
                        dangTai={apSua.isPending}
                        loi={apSua.error}
                        soDongY={soDongYSua}
                        duocApDung={q.troLyVietLai}
                        dangApDung={apDung.isPending}
                        onApDung={() => apDung.mutate({ phienId: du.id })}
                        onMoHoSoMoi={(moi) => dieuHuong(`/tham-dinh/ho-so/${moi}`)}
                        tenTheoMa={tenTheoMa}
                      />
                    </TileBody>
                  </Tile>
                )}

                {/*
                  * Hỏi–đáp: chỉ cần `tro-ly:hoi` (quyền ĐỌC, đặc tả mục 14.2). Hiện sau bản đánh giá vì
                  * người ta thường đọc tổng hợp rồi mới hỏi về chỗ chưa rõ.
                  */}
                {ds.length > 0 && q.troLyHoi && (
                  <Tile span="lg:col-span-8">
                    <TileHeader icon={<MessageOutlined />} tone="teal" title={t('thamDinh.raSoat.troLy')} />
                    <TileBody>
                      <HopTroLy phienId={du.id} />
                    </TileBody>
                  </Tile>
                )}

                {ds.length > 0 && (q.troLyHoi || q.troLyVietLai) && (
                  <Tile span="lg:col-span-8">
                    <TileHeader icon={<RobotOutlined />} tone="violet" title={t('thamDinh.raSoat.danhGia')} />
                    <TileBody>
                      <BanDanhGiaAi
                        ban={danhGia.data}
                        dangTai={danhGia.isPending}
                        loi={danhGia.error}
                        duocVietLai={q.troLyVietLai}
                        dangVietLai={vietLai.isPending}
                        onVietLai={() => vietLai.mutate(du.id)}
                        tenTheoMa={tenTheoMa}
                      />
                    </TileBody>
                  </Tile>
                )}

                <div className="col-span-12 space-y-4 lg:col-span-4">
                  <Tile>
                    <TileHeader icon={<CheckCircleOutlined />} tone="teal" title={t('thamDinh.raSoat.ketLuan')} />
                    <TileBody className="space-y-3">
                      <Dong nhan={t('thamDinh.phien.cotTrangThai')}>
                        <Badge variant={TRANG_THAI_PHIEN[du.trang_thai as TrangThaiPhien].variant}>
                          {t(TRANG_THAI_PHIEN[du.trang_thai as TrangThaiPhien].khoa)}
                        </Badge>
                      </Dong>
                      <Dong nhan={t('thamDinh.phien.cotDiem')}>
                        {du.diem === null ? KHONG_CO : soVN(du.diem, locale)}
                      </Dong>
                      <Dong nhan={t('thamDinh.phien.cotKetLuan')}>
                        {du.ket_luan
                          ? <Badge variant={KET_LUAN[du.ket_luan].variant}>{t(KET_LUAN[du.ket_luan].khoa)}</Badge>
                          : KHONG_CO}
                      </Dong>
                      <Dong nhan={t('thamDinh.phien.cotCapNhat')}>{ngayGioVN(du.created_at, locale)}</Dong>

                      {sua.error && (
                        <p className="rounded border border-danger-200 bg-danger-50 p-2 text-sm text-danger-700">
                          {dichLoi(sua.error)}
                        </p>
                      )}

                      {q.phienSua && du.trang_thai === 'cho_duyet' && (
                        <>
                          {conPhaiXem.length > 0 ? (
                            <p className="rounded border border-warning-200 bg-warning-50 p-2 text-sm text-warning-800">
                              {t('thamDinh.raSoat.chuaXemHet', { so: soVN(conPhaiXem.length, locale) })}
                            </p>
                          ) : (
                            <Button
                              variant="primary"
                              size="md"
                              className="w-full"
                              loading={hoanTat.isPending}
                              onClick={() => hoanTat.mutate(du.id)}
                            >
                              {t('thamDinh.raSoat.hoanTat')}
                            </Button>
                          )}
                          {hoanTat.error && (
                            <p className="text-sm text-danger-700">{dichLoi(hoanTat.error)}</p>
                          )}
                        </>
                      )}

                      {q.pheDuyetSua && du.trang_thai === 'cho_phe_duyet' && (
                        <div className="space-y-2">
                          <Button
                            variant="primary"
                            size="md"
                            className="w-full"
                            icon={<CheckCircleOutlined />}
                            onClick={() => setHopYKien('duyet')}
                          >
                            {t('thamDinh.raSoat.pheDuyet')}
                          </Button>
                          <Button
                            variant="outline"
                            size="md"
                            className="w-full"
                            icon={<RollbackOutlined />}
                            onClick={() => setHopYKien('tra-lai')}
                          >
                            {t('thamDinh.raSoat.traLai')}
                          </Button>
                        </div>
                      )}
                    </TileBody>
                  </Tile>

                  <Tile>
                    <TileHeader icon={<FileProtectOutlined />} tone="violet" title={t('thamDinh.raSoat.tep')} />
                    <TileBody className="p-0">
                      <ul className="divide-y divide-default">
                        {du.tep.map((f) => (
                          <li key={f.id} className="px-4 py-2">
                            <p className="truncate text-sm text-content-primary">{f.ten}</p>
                            <p className="text-2xs text-content-muted">
                              {dungLuongVN(f.kich_thuoc, locale)}
                              {f.so_ky_tu > 0 && ` · ${t('thamDinh.raSoat.soKyTu', { so: soVN(f.so_ky_tu, locale) })}`}
                            </p>
                            {f.loi && <p className="mt-1 text-2xs text-danger-700">{f.loi}</p>}
                          </li>
                        ))}
                      </ul>
                    </TileBody>
                  </Tile>
                </div>
              </div>
            </div>
          )}
        </VungDuLieu>
      </div>

      {p && (
        <HopNopLai
          open={moNopLai}
          onClose={() => setMoNopLai(false)}
          phienId={p.id}
          tieuDeCu={p.tieu_de}
          onXong={(moi) => dieuHuong(`/tham-dinh/ho-so/${moi}`)}
        />
      )}

      {p && (
        <HopSoSanh
          open={moSoSanh}
          onClose={() => setMoSoSanh(false)}
          phienId={p.id}
          coLanTruoc={!!p.phien_truoc_id}
        />
      )}

      {p && (
        <HopYKien
          open={Boolean(hopYKien)}
          traLai={hopYKien === 'tra-lai'}
          dangChay={duyet.isPending}
          loi={duyet.error}
          onClose={() => { setHopYKien(undefined); duyet.reset(); }}
          onGui={(yKien) =>
            duyet.mutate({ id: p.id, yKien, traLai: hopYKien === 'tra-lai' },
              { onSuccess: () => setHopYKien(undefined) })}
        />
      )}
    </div>
  );
}

function Dong({ nhan, children }: { nhan: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 border-b border-default pb-2 last:border-0">
      <span className="w-28 shrink-0 text-xs text-content-muted">{nhan}</span>
      <span className="min-w-0 flex-1 text-sm text-content-primary">{children}</span>
    </div>
  );
}
